import React, { useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import '../../LoginDialog.css';

const SESSION_PERSISTENCE_DAYS = 7;

function resolveRedirectTarget(locationState) {
  if (locationState && typeof locationState === 'object' && typeof locationState.from === 'string' && locationState.from.startsWith('/')) {
    return locationState.from;
  }
  return '/dashboard';
}

function LoginPage({ user, setUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState('login');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [signupLogin, setSignupLogin] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupPasswordConfirm, setSignupPasswordConfirm] = useState('');
  const [error, setError] = useState('');
  const [signupError, setSignupError] = useState('');
  const [loading, setLoading] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const redirectTarget = useMemo(() => resolveRedirectTarget(location.state), [location.state]);
  const isLoginMode = mode === 'login';
  const sessionNotice = `Une fois connecte, votre session reste active ${SESSION_PERSISTENCE_DAYS} jours sur cet appareil, sauf deconnexion manuelle.`;

  if (user) {
    return <Navigate to={redirectTarget} replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!login.trim() || !password) {
      setError('Le login et le mot de passe sont obligatoires.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/users/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login: login.trim(), password }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const payload = await response.json();
      if (!payload?.success) {
        setError(payload?.message || 'Login ou mot de passe invalide.');
        return;
      }

      setUser({
        login: payload.login,
        name: payload.login,
        role: payload.role || 'USER',
      });
      navigate(redirectTarget, { replace: true });
    } catch (submissionError) {
      setError(submissionError instanceof Error ? submissionError.message : 'Connexion impossible pour le moment.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignupSubmit = async (event) => {
    event.preventDefault();
    if (!signupLogin.trim() || !signupEmail.trim() || !signupPassword || !signupPasswordConfirm) {
      setSignupError('Le login, l’email et le mot de passe sont obligatoires.');
      return;
    }
    if (signupPassword !== signupPasswordConfirm) {
      setSignupError('Les mots de passe ne correspondent pas.');
      return;
    }

    setSignupLoading(true);
    setSignupError('');

    try {
      const response = await fetch('/api/users/register', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          login: signupLogin.trim(),
          email: signupEmail.trim(),
          password: signupPassword,
        }),
      });

      const payload = await response.json();
      if (!response.ok || !payload?.success) {
        setSignupError(payload?.message || `HTTP ${response.status}`);
        return;
      }

      setUser({
        login: payload.login,
        name: payload.login,
        role: payload.role || 'USER',
      });
      navigate(redirectTarget, { replace: true });
    } catch (submissionError) {
      setSignupError(submissionError instanceof Error ? submissionError.message : 'Inscription impossible pour le moment.');
    } finally {
      setSignupLoading(false);
    }
  };

  return (
    <div className="login-page-shell">
      <section className="login-page-card">
        <div className="login-page-copy">
          <span className="login-page-eyebrow">Authentification</span>
          <h1 className="login-page-title">{isLoginMode ? 'Connectez-vous au cockpit EcotoneAI' : 'Créez votre compte EcotoneAI'}</h1>
          <p className="login-page-subtitle">
            {isLoginMode
              ? 'Retrouvez vos réglages, vos connexions (exchanges et wallets) et les préférences liées à votre session.'
              : 'Ouvrez votre accès pour retrouver vos réglages, vos connexions et vos préférences de session.'}
          </p>
          <p className="login-page-session-note">{sessionNotice}</p>
        </div>

        <div>
          <div className="login-dialog-switch" role="tablist" aria-label="Choix authentification">
            <button type="button" className={isLoginMode ? 'active' : ''} onClick={() => setMode('login')}>Se connecter</button>
            <button type="button" className={!isLoginMode ? 'active' : ''} onClick={() => setMode('signup')}>Créer un compte</button>
          </div>

          {isLoginMode ? (
            <form className="login-form" onSubmit={handleSubmit}>
              <label className="login-page-field">
                <span>Login</span>
                <input
                  type="text"
                  name="login"
                  autoComplete="username"
                  value={login}
                  onChange={(event) => setLogin(event.target.value)}
                  placeholder="Votre login"
                />
              </label>

              <label className="login-page-field">
                <span>Mot de passe</span>
                <input
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Votre mot de passe"
                />
              </label>

              {error ? <div className="login-page-error">{error}</div> : null}

              <button type="submit" disabled={loading}>
                {loading ? 'Connexion en cours...' : 'Se connecter'}
              </button>
            </form>
          ) : (
            <form className="login-form signup-form" onSubmit={handleSignupSubmit}>
              <label className="login-page-field">
                <span>Login</span>
                <input
                  type="text"
                  name="signup-login"
                  autoComplete="username"
                  value={signupLogin}
                  onChange={(event) => setSignupLogin(event.target.value)}
                  placeholder="Choisissez un login"
                />
              </label>

              <label className="login-page-field">
                <span>Adresse email</span>
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={signupEmail}
                  onChange={(event) => setSignupEmail(event.target.value)}
                  placeholder="vous@exemple.com"
                />
              </label>

              <label className="login-page-field">
                <span>Mot de passe</span>
                <input
                  type="password"
                  name="signup-password"
                  autoComplete="new-password"
                  value={signupPassword}
                  onChange={(event) => setSignupPassword(event.target.value)}
                  placeholder="Au moins 8 caractères"
                />
              </label>

              <label className="login-page-field">
                <span>Confirmer le mot de passe</span>
                <input
                  type="password"
                  name="signup-password-confirm"
                  autoComplete="new-password"
                  value={signupPasswordConfirm}
                  onChange={(event) => setSignupPasswordConfirm(event.target.value)}
                  placeholder="Répétez votre mot de passe"
                />
              </label>

              {signupError ? <div className="login-page-error">{signupError}</div> : null}

              <button type="submit" disabled={signupLoading}>
                {signupLoading ? 'Inscription en cours...' : 'Créer mon compte'}
              </button>
            </form>
          )}
        </div>

        <div className="login-page-footer">
          <button type="button" className="login-page-link-button" onClick={() => setMode(isLoginMode ? 'signup' : 'login')}>
            {isLoginMode ? 'Pas encore de compte ? Créer un compte' : 'Déjà inscrit ? Se connecter'}
          </button>
          <Link to="/">Retour à l’accueil</Link>
        </div>
      </section>
    </div>
  );
}

export default LoginPage;